import { useMutation } from '@apollo/client/react';
import { Grid, Box, Typography, Button, Chip, ToggleButtonGroup, ToggleButton } from '@mui/material';
import dayjs from 'dayjs';
import React, { useContext, useState } from 'react';

import CurrentUserContext from '@/Contexts/CurrentUser';
import { CreateCheckoutSessionDocument, CreateBillingPortalSessionDocument } from '@/graphql/generated/graphql';
import { displayNotification } from '@/utils/notifications';

const STATUS_LABELS: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
  active: { label: 'Active', color: 'success' },
  past_due: { label: 'Past due', color: 'warning' },
  canceled: { label: 'Canceled', color: 'error' },
  incomplete: { label: 'Incomplete', color: 'default' },
};

const SubscriptionSection: React.FC = () => {
  const { currentUser, dateTimePreference } = useContext(CurrentUserContext);
  const [interval, setInterval] = useState<'monthly' | 'yearly'>('monthly');

  const [createCheckoutSession, { loading: creatingCheckout }] = useMutation(CreateCheckoutSessionDocument);
  const [createBillingPortalSession, { loading: openingPortal }] = useMutation(CreateBillingPortalSessionDocument);

  const subscription = currentUser?.defaultEmployment?.subscription;

  const handleUpgrade = () => {
    createCheckoutSession({
      variables: { interval },
      onCompleted: (data) => {
        if (data.createCheckoutSession) {
          window.location.href = data.createCheckoutSession;
        } else {
          displayNotification({ type: 'error', text: 'Could not start checkout. Please try again.' });
        }
      },
      onError: (error) =>
        displayNotification({ type: 'error', text: error.message || 'Could not start checkout. Please try again.' }),
    });
  };

  const handleManageBilling = () => {
    createBillingPortalSession({
      variables: {},
      onCompleted: (data) => {
        if (data.createBillingPortalSession) {
          window.location.href = data.createBillingPortalSession;
        } else {
          displayNotification({ type: 'error', text: 'Could not open billing portal. Please try again.' });
        }
      },
      onError: (error) =>
        displayNotification({
          type: 'error',
          text: error.message || 'Could not open billing portal. Please try again.',
        }),
    });
  };

  if (!subscription) {
    return (
      <Grid container direction="column" sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No subscription found.
        </Typography>
      </Grid>
    );
  }

  const statusMeta = STATUS_LABELS[subscription.status] ?? { label: subscription.status, color: 'default' as const };
  const isPaid = subscription.planType === 'paid';
  const periodEndLabel = subscription.currentPeriodEnd
    ? dayjs(String(subscription.currentPeriodEnd)).format(dateTimePreference.date)
    : null;

  return (
    <Grid container direction="column" sx={{ p: 2, gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {isPaid ? `Paid Plan (${subscription.interval === 'yearly' ? 'Yearly' : 'Monthly'})` : 'Free Plan'}
        </Typography>
        <Chip size="small" label={statusMeta.label} color={statusMeta.color} />
      </Box>

      {isPaid && periodEndLabel && (
        <Typography variant="body2" color="text.secondary">
          {subscription.cancelAtPeriodEnd ? `Your plan will end on ${periodEndLabel}.` : `Renews on ${periodEndLabel}.`}
        </Typography>
      )}

      {isPaid ? (
        <Button
          variant="outlined"
          sx={{ alignSelf: 'flex-start' }}
          onClick={handleManageBilling}
          disabled={openingPortal}
        >
          {openingPortal ? 'Opening...' : 'Manage Billing'}
        </Button>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'flex-start' }}>
          <Typography variant="body2" color="text.secondary">
            Upgrade to unlock full access.
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={interval}
            onChange={(_, value) => value && setInterval(value)}
          >
            <ToggleButton value="monthly">£10 / Month</ToggleButton>
            <ToggleButton value="yearly">£120 / Year</ToggleButton>
          </ToggleButtonGroup>
          <Button variant="contained" onClick={handleUpgrade} disabled={creatingCheckout}>
            {creatingCheckout ? 'Redirecting...' : `Upgrade — ${interval === 'monthly' ? 'Monthly' : 'Yearly'}`}
          </Button>
        </Box>
      )}
    </Grid>
  );
};

export default SubscriptionSection;
